import cx from 'classnames';
import _ from 'lodash';
import React, { ReactNode } from 'react';
import { getUniqueKey } from '../_utils';
import { Button } from '../button';
import { IconID } from '../icon';
import { Input } from '../input';
import { Menu } from '../menu';
import { MenuItemEventHandler } from '../menu/menu-item';
import { Popover } from '../popover';
import { ContentWidth } from '../popover/common';
import { Colors, Typography } from '../typography';
import styles from './select.module.scss';
import { LoaderGlobal } from '../loading-indicator';

/** Option Item Props */
export interface SelectOption {
  id: string;
  label: string;
  children?: ReactNode;
  data?: any;
  disabled?: boolean;
  tooltip?: string;
  onSelect?: (data: MenuItemEventHandler) => void;
  subText?: string;
  rightText?: string;
  iconName?: IconID;
  /**
   * Image path to show in case selected option needs to be represented as image
   */
  imagePath?: string;
  active?: boolean;
}

export interface SelectOptionTitleButtonConfig {
  /**
   * icon to display
   */
  icon: IconID;

  /**
   * button click handler
   */
  onClick?: () => void;
}

export interface SelectedValue {
  id: string;
  label: string;
  imagePath: string;
  subText?: string;
}

export interface OptionsGroup {
  /**
   * option group title
   */
  title?: string;
  /**
   * option group title button
   */
  titleButton?: SelectOptionTitleButtonConfig;
  /**
   * option group empty state
   */
  emptyState?: React.ReactNode;
  /**
   * Select options
   */
  options: SelectOption[];
}

export enum SELECT_DATA_TEST_ID {
  DROPDOWN_SEARCH = 'dropdown-search',
  EMPTY_OPTION = 'empty-option',
  DROPDOWN_FOOTER = 'dropdown-footer',
}

export interface SelectClassTypes {
  selectHeadClass?: string;
  selectCaret?: string;
  selectValue?: string;
  selectBodyClass?: string;
}

export interface ResetOptionInfo {
  showResetSelectedOption: boolean;
  resetSelectedOptionLabel: string;
  resetDataInfo?: any;
}

export type SelectType = 'menu' | 'dropdown';

export interface SelectProps {
  /**
   * list of options passed to be rendered
   */
  optionsGroups: OptionsGroup[];

  /**
   * Unique id to identify the select.
   * @default ''
   */
  id?: string;

  /** Initial place holder text
   * @default ''
   */
  placeholder?: string;

  /**
   * intial flag value to disable component
   * @default false
   */
  disabled?: boolean;

  /** prop to be passed to input control to enabled compact field style
   * @default false
   */
  isCompactField?: boolean;

  /** prop to be passed to input control to enabled greybackground
   * @default false
   */
  hasGrayBackground?: boolean;

  /**
   * class to have customize style.
   * Takes an object, need to override the following classnames inside the classList object:
   *
   * `selectHeadClass?: string; // For Header styling`
   * `selectCaret?: string;     // For caret styling`
   * `selectValue?: string;     // For Selected Value styling`
   * `selectBodyClass?: string; // For Select Body styling`
   * @default ''
   */
  classList?: SelectClassTypes;

  /** enable visibility of input control
   * used for filter list
   * @default false
   */
  isSearchEnabled?: boolean;

  /** call back event if the option is selected
   * @default noop
   */
  onChange?: (data: SelectOption | SelectOption[]) => void;

  /** flag to toggle visibility of menu */
  isMenuVisible?: boolean;

  /** type of select */
  type?: SelectType;

  /** Multiselect flag for select menu
   * Note to show selected Options both multiSelect and selectedOptions
   * should be passed as props
   * @default false
   */
  multiSelect?: boolean;

  /** default selected options
   * should be array of Select Options Id
   */
  selectedOptions?: string[];

  /** call back event when menu is visible
   * @default noop
   */
  onMenuToggle?: (visible: boolean) => void;

  /** call back event when input in search box
   * @default noop
   */
  onSearchInput?: (term: string) => void;

  /**
   * No records component as a renderEmpty
   */
  emptyText?: string;

  /**
   * Variants for dropdown - currently supports rounded header
   * @default 'default'
   */
  variant?: 'default' | 'roundedHead' | 'richSubtext';

  /**
   * Font style for the header label
   * @default 'default'
   */
  headerFontStyle?: 'default' | 'bold';

  /**
   * Footer component to be shown at the end of dropdown
   */

  footer?: React.ReactElement[] | React.ReactNode;

  /**
   * Make the footer component sticky
   */

  stickyFooter?: boolean;

  /**
   * showFooterIfNoData flag to be shown the footer end of dropdown when there is NO data
   * @default false
   */

  showFooterIfNoData?: boolean;

  /**
   * Boolean to show image as selected value
   * @default false
   */
  shouldShowSelectedImage?: boolean;

  /**
   * to show loading...
   */
  loading?: boolean;

  /**
   * hasError: To show error state
   * @default false
   */
  hasError?: boolean;

  /**
   * hasWarning: To show warning state
   * @default false
   */
  hasWarning?: boolean;

  /**
   * error message/text
   */
  helperText?: string;

  /**
   * Width of dropdown:
   * undefined - As per content
   * "targetWidth" - Same as target's width
   * "minTargetWidth" - Minimum of target's width and expand with content
   * "defaultwidth" - Used only for internal purposes only. This will be the defult value for select-dropdown and it is passed to popover as per following:
   *
   * "singleSelectWidth" - In case of single-select - Same as target's width and expand with content. Max-width will be 240px.
   * "multiSelectWidth" - In case of multi-select - Same as target's width and expand with content. Max-width will be 320px.
   * "singleSelectWithSearchBarWidth" - In case of single-select with search bar - Fixed width at 240px. If target's width is greater, than width will be same as target's width.
   * "multiSelectWithSearchBarWidth" - In case of multi-select with search bar - Fixed width at 320px. If target's width is greater, than width will be same as target's width.
   */
  dropdownWidth?: 'defaultWidth' | 'targetWidth' | 'minTargetWidth';

  /**
   * Bottom offset. Gap between the target and the popover body.
   * For internal use only.
   * @default 0
   */
  bottomOffset?: number;
  /**
   * No input option to reset selected option
   */
  resetSelectedOption?: ResetOptionInfo;

  /**
   * Shows tooltip on the option label
   */
  showLabelTooltip?: boolean;

  /**
   * Limit at which the dropdown item would truncate
   */
  labelTruncationLimit?: number;

  /**
   * can search is enable for sub text
   */
  canSearchSubText?: boolean;

  /**
   * Enables a rich subtext display, including a multiline header
   * in the dropdown and highlighted subtext in menu items.
   */
  enableRichSubtext?: boolean;

  PreviousSelectedObjects?: any;
  /**
   * render custom dropdown content
   */
  customDropdownContent?: React.ReactNode;
  /**
   * render custom dropdown head label
   */
  customCurrentSelectionDisplay?: React.ReactNode;
  /**
   * should enable scroll in popover
   */
  enableScroll?: boolean;

  /**
   * callback to handle key press events on menu item
   */
  onKeyPressCB?: (event: React.KeyboardEvent) => void;
  /**
   * should auto focus search when opening dropdown
   */
  shouldAutoFocusSearch?: boolean;
  /**
   * Ref to select menu
   */
  menuRef?: React.RefObject<HTMLDivElement>;
  /**
   * Ref to select header
   */
  selectHeaderRef?: React.RefObject<HTMLDivElement>;

  /**
   * Ref to Input
   */
  inputRef?: React.RefObject<HTMLInputElement>;
  /**
   * Should handle keyboard navigation if not handled by component
   * @default false
   */
  enableKeyboardNavigation?: boolean;
  /**
   * Ref to Footer
   */
  footerRef?: React.RefObject<HTMLDivElement>;

  /**
   * Place holder text for search when isSearchEnabled is present
   * @default "Search..."
   */
  searchPlaceholder?: string;
}

export const defaultSelectedValue: SelectedValue = {
  id: '',
  label: '',
  imagePath: '',
  subText: '',
};

export const resetSelectedOptionId = 'reset-selected-option';

const selectContainerClass = cx(styles.select, styles.selectOpen);

/** default params definition */
type SearchTerm = (seachString: string) => void;
type SelectCallback = (option: SelectOption) => void;
const OptionHighlighted = { id: '', label: '' };
type SelectOptionRef = React.RefObject<HTMLDivElement>;
type DropDownHeaderRef = React.RefObject<HTMLDivElement>;
interface ParamListType {
  searchTerm?: string;
  setSearchTerm: SearchTerm;
  dropDownHeader?: any;
  onSelectCallback?: SelectCallback;
  optionHighlighted?: SelectOption;
  selectMenuRef?: SelectOptionRef;
  dropDownHeaderRef?: DropDownHeaderRef;
  customDropdownContent?: React.ReactNode;
  enableScroll?: boolean;
  stickyFooterClickCallback?: any;
}

export type BaseSelectProps = Pick<
  SelectProps,
  | 'optionsGroups'
  | 'id'
  | 'isSearchEnabled'
  | 'isMenuVisible'
  | 'type'
  | 'selectedOptions'
  | 'classList'
  | 'multiSelect'
  | 'emptyText'
  | 'footer'
  | 'showFooterIfNoData'
  | 'loading'
  | 'dropdownWidth'
  | 'bottomOffset'
  | 'labelTruncationLimit'
  | 'showLabelTooltip'
  | 'canSearchSubText'
  | 'PreviousSelectedObjects'
  | 'onKeyPressCB'
  | 'shouldAutoFocusSearch'
  | 'inputRef'
  | 'enableKeyboardNavigation'
  | 'footerRef'
  | 'searchPlaceholder'
  | 'stickyFooter'
  | 'variant'
>;

/**
 * factory method to create select component
 * @param selectprop : input props to create a jsx for type of select
 * @param setSearchTerm : setter function to search search term if isSearchEnabled
 * @param dropDownHeader : render header if the type === 'dropdown'
 * @param onSelectCallback : select callback from type === 'dropdown'
 * @param optionHighlighted : hightlight option state for accessibility
 * @param selectMenuRef : setRef for select menu to handle document Event hander)
 * @returns JSX factory based on the type of select props
 */
export const createBaseSelect = (
  {
    optionsGroups,
    id = '',
    classList = {
      selectHeadClass: '',
      selectCaret: '',
      selectValue: '',
      selectBodyClass: '',
    },
    isSearchEnabled = false,
    isMenuVisible = true,
    type = 'dropdown',
    selectedOptions = [],
    multiSelect = false,
    emptyText = 'No records found',
    footer,
    showFooterIfNoData = false,
    loading,
    dropdownWidth,
    bottomOffset = 0,
    showLabelTooltip,
    labelTruncationLimit,
    canSearchSubText = false,
    PreviousSelectedObjects = {},
    onKeyPressCB = _.noop,
    shouldAutoFocusSearch = false,
    inputRef,
    enableKeyboardNavigation = false,
    footerRef,
    searchPlaceholder = 'Search...',
    stickyFooter,
    variant = 'default',
    ...restProps
  }: BaseSelectProps,
  {
    searchTerm = '',
    setSearchTerm = _.noop,
    onSelectCallback = _.noop,
    dropDownHeader = null,
    optionHighlighted = OptionHighlighted,
    selectMenuRef = { current: null },
    dropDownHeaderRef = { current: null },
    customDropdownContent,
    enableScroll = true,
    stickyFooterClickCallback = _.noop,
  }: ParamListType
) => {
  const enableMultiSelect = (optionId: any) => {
    if (multiSelect) {
      return {
        iconName: selectedOptions.indexOf(optionId) >= 0 ? IconID.CHECKMARK : IconID.EMPTY,
      };
    }
    return {};
  };

  const enableRichSubtext = variant === 'richSubtext';

  const renderOptions = (optionGroup: OptionsGroup, showSeperator = true) => (
    <Menu.Group showSeparator={showSeperator} key={getUniqueKey(optionGroup)}>
      {optionGroup.title && !optionGroup.titleButton && (
        <Typography variant="p-bold" className={styles.groupTitle}>
          {optionGroup.title}
        </Typography>
      )}
      {optionGroup.title && optionGroup.titleButton && (
        <div className={styles.groupTitleWithButton}>
          <Typography variant="p-bold" noMargin>
            {optionGroup.title}
          </Typography>
          <Button
            icon={optionGroup.titleButton.icon}
            onClick={optionGroup.titleButton.onClick}
            size="xs"
            type="secondary"
            data-testid={optionGroup.titleButton.icon}
            className={styles.groupButton}
          />
        </div>
      )}
      {optionGroup.emptyState && optionGroup.options.length === 0 && (
        <div className={styles.noRecords} data-testid={SELECT_DATA_TEST_ID.EMPTY_OPTION}>
          <div className={styles.emptyState}>{optionGroup.emptyState}</div>
        </div>
      )}
      {optionGroup.options.map((option: SelectOption) => {
        const {
          id: OptionId,
          label,
          children,
          data,
          disabled: isDisabled,
          onSelect = _.noop,
          tooltip,
          subText,
          rightText,
          iconName,
          imagePath,
          active,
        } = option;
        return (
          <Menu.Item
            key={getUniqueKey(`${label}-${OptionId}`)}
            id={OptionId}
            label={label}
            data={data}
            imagePath={imagePath}
            active={(optionHighlighted && optionHighlighted.id === OptionId) || active}
            disabled={isDisabled}
            onSelect={(menuData: MenuItemEventHandler) => {
              onSelect(menuData);
              onSelectCallback(option);
              // to focus the dropdown header after selecting menu item using enter key
              dropDownHeaderRef?.current?.focus();
            }}
            {...enableMultiSelect(OptionId)}
            data-tooltip-content={tooltip}
            data-tooltip-position="center"
            data-tooltip-align="top"
            data-testid="select-option"
            subText={subText}
            rightText={rightText}
            iconName={iconName}
            showLabelTooltip={showLabelTooltip}
            labelTruncationLimit={labelTruncationLimit}
            enableRichSubtext={enableRichSubtext}
          >
            {children}
          </Menu.Item>
        );
      })}
    </Menu.Group>
  );

  const renderNoRecords = () => {
    const footerClass = cx(styles.footer, {
      [styles.withMultiselect]: multiSelect,
    });
    return (
      <div className={styles.noRecords}>
        <img
          src="/node_modules/@thoughtspot/radiant-styles/public/img/illustrations/no-result-found.svg"
          className={styles.noResultFoundIcon}
          alt="No Result Found"
        />
        <Typography variant="p-bold" color={Colors.gray} className={styles.noSuggestionText}>
          {emptyText}
        </Typography>
        {showFooterIfNoData && !!footer && <div className={footerClass}>{footer}</div>}
      </div>
    );
  };

  const renderOptionGroup = (optionGroups: OptionsGroup[]) => {
    if (!optionGroups.length) {
      return renderNoRecords();
    }

    const footerClass = cx(styles.footer, {
      [styles.withMultiselect]: multiSelect,
      [styles.stickyFooterContainer]: stickyFooter,
    });

    return (
      // createBaseSelect is only called from select-menu.tsx and select-dropdown.tsx,
      // both of which implement their own keyboard navigation, so we can disable it in Menu
      <Menu
        id={id}
        show={isMenuVisible}
        disableKeyboardNavigation={!enableKeyboardNavigation}
        onMenuItemKeyPressCB={onKeyPressCB}
      >
        {optionGroups.map((option: OptionsGroup, index: number) =>
          renderOptions(option, !(optionGroups.length - 1 === index))
        )}
        {!!footer && (
          <div
            role="button"
            className={footerClass}
            ref={footerRef}
            tabIndex={0}
            onKeyDown={onKeyPressCB}
            onClick={() => {
              if (stickyFooter) {
                stickyFooterClickCallback();
              }
            }}
            data-testid={SELECT_DATA_TEST_ID.DROPDOWN_FOOTER}
          >
            {footer}
          </div>
        )}
      </Menu>
    );
  };

  const updateSearchTerm = (searchTermString: string) => setSearchTerm(searchTermString);

  const handleInputChange = (event: any) => {
    updateSearchTerm(event.target.value);
  };

  /** TODO this will be replace by React Input Component */
  const renderSearchInput = () =>
    isMenuVisible &&
    isSearchEnabled && (
      <Input.Text
        inputRef={inputRef}
        value={searchTerm}
        prefixIcon={IconID.MAGNIFYING_GLASS}
        placeholder={searchPlaceholder}
        ariaLabel={'search-bro'}
        onChange={handleInputChange}
        className={styles.searchInput}
        data-testid={SELECT_DATA_TEST_ID.DROPDOWN_SEARCH}
        autoFocus={shouldAutoFocusSearch}
        onTab={onKeyPressCB}
        onEscape={onKeyPressCB}
      />
    );

  const renderLoader = () => {
    return (
      <div className={styles.loading}>
        {/* <Typography variant="h5" color={Colors.gray}>
          Loading
          <span className={styles.dots}>.</span>
        </Typography> */}
        <LoaderGlobal className={styles.loader} isTransparentBg={true}>
          <></>
        </LoaderGlobal>
      </div>
    );
  };

  const getContent = () => {
    return (
      customDropdownContent ?? (
        <div className={styles.popoverWrapper}>
          {renderSearchInput()}
          {loading ? renderLoader() : renderOptionGroup(optionsGroups)}
        </div>
      )
    );
  };

  const getContentWidth = () => {
    let contentWidth: ContentWidth | undefined = dropdownWidth;
    if (dropdownWidth !== 'defaultWidth') {
      return contentWidth;
    }

    if (multiSelect) {
      if (isSearchEnabled) {
        contentWidth = 'multiSelectWithSearchBarWidth';
      } else {
        contentWidth = 'multiSelectWidth';
      }
    } else if (isSearchEnabled) {
      contentWidth = 'singleSelectWithSearchBarWidth';
    } else {
      contentWidth = 'singleSelectWidth';
    }

    return contentWidth;
  };

  const popoverWrapper = () => (
    <Popover
      popperRef={selectMenuRef}
      targetRef={dropDownHeaderRef}
      content={getContent()}
      isVisible={isMenuVisible}
      align="bottom-start"
      contentWidth={getContentWidth()}
      appendToBody
      enableScroll={enableScroll}
      className={classList?.selectBodyClass}
      bottomOffset={bottomOffset}
    />
  );

  /** Use case to remove padding and margin from body class
   * if the select type is menu
   */
  const selectBodyClass = cx(styles.selectBody, {
    [styles.selectMenuBody]: type === 'menu',
    [classList.selectBodyClass!]: type === 'menu' && classList?.selectBodyClass,
  });
  return (
    // Do not add data-testid here so that the caller can
    //  pass their own data-testid
    <div className={selectContainerClass} {...restProps}>
      {dropDownHeader}
      <div className={selectBodyClass} role="button" aria-label="Expand dropdown options">
        {type === 'dropdown' ? (
          popoverWrapper()
        ) : (
          <>
            {renderSearchInput()}
            {renderOptionGroup(optionsGroups)}
          </>
        )}
      </div>
    </div>
  );
};
